import { reportPageActionToNewRelic } from '@verifime/utils';
import { useEffect } from 'react';
import type { TConfirmEntityPlanState } from '../confirmEntityPlanState';
import type { TPlanIdentity } from '../entityPlan';
import { isChosenIdentityBacked, type TPlanChosen } from '../entityPlanIdentities';

/**
 * The New Relic page action for a chosen wallet identity the wallet cannot back. The browser agent
 * does not collect console output in this deployment, so this is what engineering sees. It carries
 * `source` and `status` (constant labels): never an id or a name.
 */
export const CHOSEN_IDENTITY_UNBACKED_PAGE_ACTION = 'confirm-chosen-identity-unbacked';
const CHOSEN_IDENTITY_UNBACKED_REASON = 'chosen-identity-not-in-wallet';

/**
 * Reports, once, a chosen wallet identity the wallet cannot back: the plan then treats it as a new
 * entity. Nothing is reported for just me, for a new entity (no identity chosen) or for an identity
 * the wallet backs.
 */
export function useReportUnbackedChosen(
  state: TConfirmEntityPlanState,
  identityList: TPlanIdentity[] | undefined,
  chosen: TPlanChosen | undefined,
) {
  const isUnbacked =
    state.status === 'loaded' &&
    state.plan.choice !== 'individual' &&
    !isChosenIdentityBacked(identityList ?? [], chosen, state.plan.choice);
  useEffect(() => {
    if (isUnbacked) {
      console.error('Confirm entity plan: the chosen identity is not in the wallet', {
        reason: CHOSEN_IDENTITY_UNBACKED_REASON,
      });
      reportPageActionToNewRelic(CHOSEN_IDENTITY_UNBACKED_PAGE_ACTION, {
        source: 'confirm-entity-plan',
        status: CHOSEN_IDENTITY_UNBACKED_REASON,
      });
    }
  }, [isUnbacked]);
}
